<template>
  <div
    data-qa="core_smart_captcha_container"
    class="core-smart-captcha-container d-flex align-center justify-center"
  >
    <v-progress-circular
      v-show="!isLoadedCaptchaScript"
      size="32"
      color="primary"
      width="2"
      indeterminate
    />
    <div
      v-show="isLoadedCaptchaScript"
      ref="captchaRef"
      data-qa="smart_captcha"
    />
    <input
      v-if="fieldName && userToken"
      :name="fieldName"
      class="d-none"
      :value="userToken"
    >
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import getUserLanguage from 'src/utils/getUserLanguage'
import type { SmartCaptchaSupportedIsoLanguage } from 'www/types/yandex-smart-captcha'

const props = defineProps<{
  fieldName?: string
  scriptSrc: string
  renderParams: {
    sitekey: string
    test?: boolean
  }
}>()

const captchaRef = ref()

const captchaWidgetId = ref<number | null>(null)
const userToken = ref('')

const isPassedCaptcha = computed(() => !!userToken.value)

const isLoadedCaptchaScript = ref(false)
const supportedIsoLanguages = new Set<SmartCaptchaSupportedIsoLanguage>(['ru', 'en', 'be', 'kk', 'tt', 'uk', 'uz', 'tr'])

function isSupportedIsoLanguage(iso: string): iso is SmartCaptchaSupportedIsoLanguage {
  return (supportedIsoLanguages as Set<string>).has(iso)
}

function getCaptchaLanguage() {
  const userLanguage = getUserLanguage()

  // Значения языков взяты https://yandex.cloud/ru/docs/smartcaptcha/concepts/widget-methods
  return isSupportedIsoLanguage(userLanguage)
    ? userLanguage
    : 'ru'
}

function resetCaptcha() {
  if (!window.smartCaptcha || captchaWidgetId.value === null) {
    return
  }

  try {
    window.smartCaptcha.reset(captchaWidgetId.value)
    userToken.value = ''
  } catch (_error) { /* empty */ }
}

function renderCaptcha() {
  if (!window.smartCaptcha) {
    return
  }

  if (captchaWidgetId.value !== null) {
    try {
      window.smartCaptcha.destroy(captchaWidgetId.value)
    } catch (_error) { /* empty */ }
  }

  userToken.value = ''
  captchaWidgetId.value = window.smartCaptcha.render(captchaRef.value, {
    ...props.renderParams,
    hl: getCaptchaLanguage(),
  })
  window.smartCaptcha.subscribe(captchaWidgetId.value, 'success', token => {
    userToken.value = token
  })
}

onMounted(() => {
  const captchaScript = document.createElement('script')

  captchaScript.defer = true
  captchaScript.src = props.scriptSrc
  captchaScript.addEventListener('load', () => {
    isLoadedCaptchaScript.value = true
    renderCaptcha()

    const smartTokenInput = captchaRef.value.querySelector('input')

    if (smartTokenInput) {
      smartTokenInput.disabled = true
    }
  })

  document.head.prepend(captchaScript)
})

defineExpose({
  resetCaptcha,
  userToken,
  isPassedCaptcha,
})
</script>

<style lang="scss" scoped>
.core-smart-captcha-container {
  min-height: 102px;
}
</style>
